import { useEffect, useState, useMemo } from "react";
import { useAuth, API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { ChefHat, Plus, Pencil, Trash2, Archive, Loader2, Search, Filter, RotateCcw, Eye, Link2, X as XIcon, Copy } from "lucide-react";
import { toast } from "sonner";
import { xhrJson } from "../../utils/xhrJson";
import { Link } from "react-router-dom";

const emptyRecipe = {
  name: "", recipe_category_id: "", description: "", ingredients: "", cooking_instructions: "",
  serving_size: "", operational_unit: "units", operational_value: "", metric_unit: "", metric_value: "",
  delivery_instructions: "",
  tag_ids: [], notes: "", kitchen_ids: [], linked_recipe_ids: [],
};

export default function AdminRecipes() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "nutritionist";
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [operationalUnits, setOperationalUnits] = useState([]);
  const [metricUnits, setMetricUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", recipe_category_id: "all", kitchen_id: "all", tag_id: "all", is_archived: "false" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // Bulk selection — set of recipe_ids currently ticked. Cleared on reload
  // (so stale ids don't linger after filter changes) and on explicit "Clear".
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadDeps = async () => {
    const [c, t, k, ou, mu] = await Promise.all([
      fetch(`${API}/recipe-categories`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/recipe-tags`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/kitchens`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/recipe-operational-units`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/recipe-metric-units`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
    ]);
    setCategories(c); setTags(t); setKitchens(k); setOperationalUnits(ou); setMetricUnits(mu);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.q) params.set("q", filters.q);
      if (filters.recipe_category_id && filters.recipe_category_id !== "all") params.set("recipe_category_id", filters.recipe_category_id);
      if (filters.kitchen_id && filters.kitchen_id !== "all") params.set("kitchen_id", filters.kitchen_id);
      if (filters.tag_id && filters.tag_id !== "all") params.set("tag_id", filters.tag_id);
      if (filters.is_archived !== "any") params.set("is_archived", filters.is_archived);
      const r = await fetch(`${API}/recipes?${params.toString()}`, { credentials: "include" });
      setRecipes(r.ok ? await r.json() : []);
      // Filter change or manual reload → drop stale selection so we don't
      // accidentally bulk-act on recipes the admin can no longer see.
      setSelected(new Set());
    } finally { setLoading(false); }
  };

  useEffect(() => { loadDeps(); load(); /* eslint-disable-line */ }, []);
  // Debounced search
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [filters]);

  const catById = useMemo(() => Object.fromEntries(categories.map((c) => [c.recipe_category_id, c])), [categories]);
  const tagById = useMemo(() => Object.fromEntries(tags.map((t) => [t.recipe_tag_id, t])), [tags]);
  const kitchenById = useMemo(() => Object.fromEntries(kitchens.map((k) => [k.kitchen_id, k])), [kitchens]);

  const handleSave = async () => {
    if (!editing.name?.trim()) { toast.error("Recipe name is required"); return; }
    if (!editing.recipe_category_id) { toast.error("Pick a category"); return; }
    setSaving(true);
    try {
      const isNew = !editing.recipe_id;
      const url = isNew ? `${API}/admin/recipes` : `${API}/admin/recipes/${editing.recipe_id}`;
      // Normalise ingredients server-side too. Frontend pre-cleans each line so the
      // stored payload is consistent (no bullets, no rogue emojis) — display reads
      // the same field, so the pills always look tidy.
      const cleanedIngredients = (editing.ingredients || "")
        .split(/\n+/)
        .map((line) =>
          line
            .replace(/^[\s\u2022\u2023\u25E6\u2043\u2219\-\*\u2192\u2013\u2014>•·]+\s*/u, "")
            .replace(/^\d+[\.\)]\s+/, "")
            .replace(/[\u{1F300}-\u{1FAFF}\u{2700}-\u{27BF}]/gu, "")
            .replace(/\s+/g, " ")
            .trim()
        )
        .filter(Boolean)
        .join("\n");
      const payload = { ...editing, ingredients: cleanedIngredients };
      // Back-compat: legacy recipes only carry portion_type — promote it to operational_unit
      // so the dynamic field is always sent. Also coerce empty strings → null for the
      // numeric fields so the backend's strict numeric validation accepts them.
      if (!payload.operational_unit && payload.portion_type) {
        payload.operational_unit = payload.portion_type;
      }
      payload.operational_value = payload.operational_value === "" || payload.operational_value === undefined ? null : payload.operational_value;
      payload.metric_value = payload.metric_value === "" || payload.metric_value === undefined ? null : payload.metric_value;
      payload.metric_unit = payload.metric_unit || "";
      const { ok, data } = await xhrJson(isNew ? "POST" : "PUT", url, payload);
      if (!ok) throw new Error(data.detail || "Save failed");
      toast.success(isNew ? "Recipe created" : "Recipe updated");
      setEditing(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hard-delete this recipe? This cannot be undone.")) return;
    try {
      const { ok, data } = await xhrJson("DELETE", `${API}/admin/recipes/${id}`);
      if (!ok) throw new Error(data.detail || "Delete failed");
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.message); }
  };

  const handleArchive = async (recipe, archive) => {
    try {
      const { ok, data } = await xhrJson("PUT", `${API}/admin/recipes/${recipe.recipe_id}`, { is_archived: archive });
      if (!ok) throw new Error(data.detail || "Failed");
      toast.success(archive ? "Archived" : "Unarchived"); load();
    } catch (e) { toast.error(e.message); }
  };

  const handleDuplicate = (recipe) => {
    // Clone the recipe into the edit dialog as a NEW record. Stripping
    // recipe_id (and server-managed timestamps/archive flag) makes the
    // existing save() branch treat it as an insert. Name is suffixed
    // "(Copy)" so the source and duplicate are visually distinguishable
    // in the list right after save.
    const { recipe_id: _rid, created_at: _c, updated_at: _u, is_archived: _a, ...rest } = recipe;
    setEditing({
      ...emptyRecipe,
      ...rest,
      name: `${recipe.name || "Recipe"} (Copy)`,
      operational_unit: recipe.operational_unit || recipe.portion_type || "units",
      operational_value: recipe.operational_value ?? "",
      metric_unit: recipe.metric_unit || "",
      metric_value: recipe.metric_value ?? "",
      delivery_instructions: recipe.delivery_instructions || "",
      tag_ids: [...(recipe.tag_ids || [])],
      kitchen_ids: [...(recipe.kitchen_ids || [])],
      linked_recipe_ids: [...(recipe.linked_recipe_ids || [])],
    });
  };

  // Selection helpers — keeps `selected` in sync with the currently-loaded
  // recipes so a "select all" tick maps 1:1 to whatever the filters expose.
  const toggleSelect = (recipeId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(recipeId)) next.delete(recipeId);
      else next.add(recipeId);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const allIds = recipes.map((r) => r.recipe_id);
      const allTicked = allIds.length > 0 && allIds.every((id) => prev.has(id));
      return allTicked ? new Set() : new Set(allIds);
    });
  };
  const clearSelection = () => setSelected(new Set());

  const handleBulk = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const confirmMsg = action === "delete"
      ? `Hard-delete ${ids.length} recipe(s)? This cannot be undone.`
      : action === "archive"
        ? `Archive ${ids.length} recipe(s)?`
        : `Unarchive ${ids.length} recipe(s)?`;
    if (!window.confirm(confirmMsg)) return;
    setBulkBusy(true);
    try {
      const { ok, data } = await xhrJson("POST", `${API}/admin/recipes/bulk-action`, { action, recipe_ids: ids });
      if (!ok) throw new Error(data.detail || "Bulk action failed");
      const label = action === "delete" ? "Deleted" : action === "archive" ? "Archived" : "Unarchived";
      toast.success(`${label} ${data.affected} recipe(s)`);
      clearSelection();
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBulkBusy(false); }
  };

  return (
    <AdminLayout activeNav="recipes">
      <div className="p-4 sm:p-6 space-y-4" data-testid="admin-recipes">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><ChefHat size={18} /></div>
            <div>
              <h1 className="text-lg sm:text-xl font-extrabold text-gray-900">Recipes</h1>
              <p className="text-xs text-gray-500">Internal recipes for the kitchen team.</p>
            </div>
          </div>
          {canEdit && (
            <Button onClick={() => setEditing({ ...emptyRecipe })} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="recipe-new-btn">
              <Plus size={14} className="mr-1" /> New recipe
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 sm:items-end">
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block flex items-center gap-1"><Search size={10} /> Search</Label>
              <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Name, ingredients, instructions…" className="text-xs" data-testid="recipe-filter-q" />
            </div>
            <FilterSelect label="Category" value={filters.recipe_category_id} onChange={(v) => setFilters((p) => ({ ...p, recipe_category_id: v }))} options={[{ value: "all", label: "All" }, ...categories.map((c) => ({ value: c.recipe_category_id, label: c.name }))]} testid="recipe-filter-cat" />
            <FilterSelect label="Kitchen" value={filters.kitchen_id} onChange={(v) => setFilters((p) => ({ ...p, kitchen_id: v }))} options={[{ value: "all", label: "All" }, ...kitchens.map((k) => ({ value: k.kitchen_id, label: k.kitchen_code }))]} testid="recipe-filter-kitchen" />
            <FilterSelect label="Status" value={filters.is_archived} onChange={(v) => setFilters((p) => ({ ...p, is_archived: v }))} options={[{ value: "false", label: "Active" }, { value: "true", label: "Archived" }, { value: "any", label: "Both" }]} testid="recipe-filter-status" />
          </div>
          {(filters.q || filters.recipe_category_id !== "all" || filters.kitchen_id !== "all" || filters.is_archived !== "false") && (
            <Button variant="outline" size="sm" className="mt-3 text-[10px]" onClick={() => setFilters({ q: "", recipe_category_id: "all", kitchen_id: "all", tag_id: "all", is_archived: "false" })}>
              <RotateCcw size={11} className="mr-1" /> Reset filters
            </Button>
          )}
        </div>

        {/* Bulk action bar — appears only when at least one recipe is ticked. */}
        {canEdit && selected.size > 0 && (
          <div
            className="sticky top-2 z-20 bg-emerald-600 text-white rounded-2xl shadow-lg shadow-emerald-900/20 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap"
            data-testid="recipe-bulk-bar"
          >
            <div className="text-xs font-semibold" data-testid="recipe-bulk-count">
              {selected.size} selected
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="secondary"
                className="h-7 text-[11px] bg-white/95 text-emerald-800 hover:bg-white"
                disabled={bulkBusy}
                onClick={() => handleBulk("archive")}
                data-testid="recipe-bulk-archive"
              >
                <Archive size={11} className="mr-1" /> Archive
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="h-7 text-[11px] bg-white/95 text-emerald-800 hover:bg-white"
                disabled={bulkBusy}
                onClick={() => handleBulk("unarchive")}
                data-testid="recipe-bulk-unarchive"
              >
                <RotateCcw size={11} className="mr-1" /> Unarchive
              </Button>
              {user?.role === "admin" && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-7 text-[11px] bg-red-50 text-red-700 hover:bg-red-100"
                  disabled={bulkBusy}
                  onClick={() => handleBulk("delete")}
                  data-testid="recipe-bulk-delete"
                >
                  <Trash2 size={11} className="mr-1" /> Delete
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-[11px] text-white/90 hover:bg-emerald-500 hover:text-white"
                onClick={clearSelection}
                data-testid="recipe-bulk-clear"
              >
                <XIcon size={11} className="mr-1" /> Clear
              </Button>
            </div>
          </div>
        )}

        {/* List */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400"><Loader2 className="animate-spin inline" size={16} /></div>
          ) : recipes.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No recipes match your filters. {recipes.length === 0 && categories.length === 0 ? "Create a category first." : "Create one above."}</div>
          ) : (
            <>
              {canEdit && (
                <div className="px-3 sm:px-4 py-2 border-b border-gray-100 flex items-center gap-3 bg-gray-50/60">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    checked={recipes.length > 0 && recipes.every((r) => selected.has(r.recipe_id))}
                    onChange={toggleSelectAll}
                    aria-label="Select all recipes"
                    data-testid="recipe-select-all"
                  />
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                    {selected.size > 0 ? `${selected.size} of ${recipes.length}` : `Select all ${recipes.length}`}
                  </span>
                </div>
              )}
              <ul className="divide-y divide-gray-100">
                {recipes.map((r) => (
                  <li key={r.recipe_id} className="p-3 sm:p-4 flex items-start gap-3" data-testid={`recipe-row-${r.recipe_id}`}>
                    {canEdit && (
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer flex-shrink-0"
                        checked={selected.has(r.recipe_id)}
                        onChange={() => toggleSelect(r.recipe_id)}
                        aria-label={`Select ${r.name}`}
                        data-testid={`recipe-select-${r.recipe_id}`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-sm text-gray-900">{r.name}</h3>
                      {r.is_archived && <span className="text-[9px] uppercase font-bold bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Archived</span>}
                      <span className="text-[10px] text-gray-400">· {catById[r.recipe_category_id]?.name || "—"}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {r.serving_size && <>Serves: {r.serving_size} · </>}
                      {(r.operational_value || r.operational_value === 0) && (r.operational_unit || r.portion_type) ? (
                        <>Op: {r.operational_value} {r.operational_unit || r.portion_type}</>
                      ) : (
                        <>Op: {r.operational_unit || r.portion_type}</>
                      )}
                      {r.metric_unit && (
                        <> · Metric: {r.metric_value ?? ""} {r.metric_unit}</>
                      )}
                    </p>
                    {(r.tag_ids?.length > 0 || r.kitchen_ids?.length > 0) && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(r.tag_ids || []).map((tid) => {
                          const t = tagById[tid]; if (!t) return null;
                          return <span key={tid} className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${t.color_hex}20`, color: t.color_hex }}>{t.name}</span>;
                        })}
                        {(r.kitchen_ids || []).map((kid) => {
                          const k = kitchenById[kid]; if (!k) return null;
                          return <span key={kid} className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{k.kitchen_code}</span>;
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Link to={`/admin/recipes/${r.recipe_id}/preview`} data-testid={`recipe-view-${r.recipe_id}`}>
                      <Button size="sm" variant="outline" className="h-7 text-[10px] text-emerald-700 border-emerald-200 hover:bg-emerald-50" title="Preview as a cook would see it">
                        <Eye size={10} className="mr-1" /> View
                      </Button>
                    </Link>
                    {canEdit && (
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setEditing({ ...r, operational_unit: r.operational_unit || r.portion_type || "units", operational_value: r.operational_value ?? "", metric_unit: r.metric_unit || "", metric_value: r.metric_value ?? "", delivery_instructions: r.delivery_instructions || "" })} data-testid={`recipe-edit-${r.recipe_id}`}><Pencil size={10} /></Button>
                    )}
                    {canEdit && (
                      <Button size="sm" variant="outline" className="h-7 text-[10px] text-blue-700 border-blue-200 hover:bg-blue-50" onClick={() => handleDuplicate(r)} title="Duplicate recipe" data-testid={`recipe-duplicate-${r.recipe_id}`}><Copy size={10} /></Button>
                    )}
                    {canEdit && (
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => handleArchive(r, !r.is_archived)} data-testid={`recipe-archive-${r.recipe_id}`}><Archive size={10} /></Button>
                    )}
                    {user?.role === "admin" && (
                      <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-600 border-red-200" onClick={() => handleDelete(r.recipe_id)} data-testid={`recipe-delete-${r.recipe_id}`}><Trash2 size={10} /></Button>
                    )}
                  </div>
                </li>
              ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.recipe_id ? "Edit recipe" : "New recipe"}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Fill in the details below. Ingredients become pills and instructions are auto-numbered into steps on the recipe card.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Recipe name *</Label>
                  <Input value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} data-testid="recipe-form-name" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Category *</Label>
                  <Select value={editing.recipe_category_id || ""} onValueChange={(v) => setEditing((p) => ({ ...p, recipe_category_id: v }))}>
                    <SelectTrigger className="text-xs" data-testid="recipe-form-cat"><SelectValue placeholder="Pick category…" /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.recipe_category_id} value={c.recipe_category_id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Description</Label>
                <Textarea
                  rows={2}
                  value={editing.description || ""}
                  onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))}
                  className="text-xs resize-y min-h-[60px]"
                  placeholder="Short summary that appears at the top of the recipe card (1-2 sentences)."
                  data-testid="recipe-form-description"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block flex items-center justify-between">
                  <span>Ingredients</span>
                  <span className="text-[10px] text-gray-400 normal-case">One per line — they&apos;ll appear as pills on the recipe card</span>
                </Label>
                <Textarea rows={5} value={editing.ingredients} onChange={(e) => setEditing((p) => ({ ...p, ingredients: e.target.value }))} className="font-mono text-xs resize-y min-h-[120px]" placeholder={"Paneer 200g\nButter 50g\nTomato 4\n…"} data-testid="recipe-form-ingredients" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block flex items-center justify-between">
                  <span>Cooking instructions</span>
                  <span className="text-[10px] text-gray-400 normal-case">Leave a blank line between steps to number them automatically</span>
                </Label>
                <Textarea rows={5} value={editing.cooking_instructions} onChange={(e) => setEditing((p) => ({ ...p, cooking_instructions: e.target.value }))} className="font-mono text-xs resize-y min-h-[120px]" placeholder={"Heat butter in a pan.\n\nAdd onions, sauté until golden.\n\nAdd tomato puree and simmer for 10 minutes."} data-testid="recipe-form-instructions" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Serving size</Label>
                  <Input value={editing.serving_size} onChange={(e) => setEditing((p) => ({ ...p, serving_size: e.target.value }))} placeholder="e.g. 4 people" data-testid="recipe-form-serving" />
                </div>
              </div>
              {/* Operational + Metric unit/value pairs */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Operational unit</Label>
                    <Select value={editing.operational_unit || ""} onValueChange={(v) => setEditing((p) => ({ ...p, operational_unit: v }))}>
                      <SelectTrigger className="text-xs bg-white" data-testid="recipe-form-operational-unit"><SelectValue placeholder="Pick…" /></SelectTrigger>
                      <SelectContent>
                        {operationalUnits.map((u) => <SelectItem key={u.rou_id} value={u.name}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {operationalUnits.length === 0 && (
                      <p className="text-[10px] text-amber-600 mt-1">No options yet — add some in Recipe Operational Units.</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Operational value</Label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={editing.operational_value ?? ""}
                      onChange={(e) => setEditing((p) => ({ ...p, operational_value: e.target.value }))}
                      placeholder="e.g. 2"
                      className="bg-white"
                      data-testid="recipe-form-operational-value"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Metric unit</Label>
                    <Select value={editing.metric_unit || "__none__"} onValueChange={(v) => setEditing((p) => ({ ...p, metric_unit: v === "__none__" ? "" : v }))}>
                      <SelectTrigger className="text-xs bg-white" data-testid="recipe-form-metric-unit"><SelectValue placeholder="Pick…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {metricUnits.map((u) => <SelectItem key={u.rmu_id} value={u.name}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {metricUnits.length === 0 && (
                      <p className="text-[10px] text-amber-600 mt-1">No options yet — add some in Recipe Metric Units.</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Metric value</Label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={editing.metric_value ?? ""}
                      onChange={(e) => setEditing((p) => ({ ...p, metric_value: e.target.value }))}
                      placeholder="e.g. 500"
                      className="bg-white"
                      data-testid="recipe-form-metric-value"
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Tags</Label>
                <PillMulti
                  items={tags}
                  selected={editing.tag_ids || []}
                  onChange={(ids) => setEditing((p) => ({ ...p, tag_ids: ids }))}
                  keyOf={(t) => t.recipe_tag_id}
                  labelOf={(t) => t.name}
                  colorOf={(t) => t.color_hex}
                  testidPrefix="recipe-form-tag"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Kitchens (leave empty = global)</Label>
                <PillMulti
                  items={kitchens}
                  selected={editing.kitchen_ids || []}
                  onChange={(ids) => setEditing((p) => ({ ...p, kitchen_ids: ids }))}
                  keyOf={(k) => k.kitchen_id}
                  labelOf={(k) => k.kitchen_code}
                  testidPrefix="recipe-form-kitchen"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block flex items-center justify-between">
                  <span>Linked recipes</span>
                  <span className="text-[10px] text-gray-400 normal-case">Search by name. Selected recipes appear stacked below.</span>
                </Label>
                <LinkedRecipePicker
                  excludeId={editing.recipe_id}
                  selectedIds={editing.linked_recipe_ids || []}
                  onChange={(ids) => setEditing((p) => ({ ...p, linked_recipe_ids: ids }))}
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>
                  Delivery instructions
                  <span className="text-[10px] text-gray-400 font-normal normal-case">(highlighted to cooks / chefs / nutritionists on the recipe detail page)</span>
                </Label>
                <Textarea rows={3} value={editing.delivery_instructions} onChange={(e) => setEditing((p) => ({ ...p, delivery_instructions: e.target.value }))} className="text-xs resize-y min-h-[80px]" placeholder={"e.g. Pack hot in foil tray, label allergen.\nUse Bain Marie tray 2.\nKeep below 4°C if delayed beyond 30 min."} data-testid="recipe-form-delivery-instructions" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Notes</Label>
                <Textarea rows={2} value={editing.notes} onChange={(e) => setEditing((p) => ({ ...p, notes: e.target.value }))} className="text-xs resize-y min-h-[60px]" data-testid="recipe-form-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="recipe-form-save">
              {saving ? <Loader2 size={13} className="animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block flex items-center gap-1"><Filter size={10} /> {label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function PillMulti({ items, selected, onChange, keyOf, labelOf, colorOf, testidPrefix }) {
  if (!items.length) return <p className="text-[10px] text-gray-400">No options yet.</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => {
        const id = keyOf(it);
        const on = selected.includes(id);
        const c = colorOf ? colorOf(it) : "#10B981";
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(on ? selected.filter((x) => x !== id) : [...selected, id])}
            className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${on ? "border-transparent" : "border-gray-200 bg-white text-gray-600 hover:border-gray-400"}`}
            style={on ? { backgroundColor: `${c}20`, color: c, borderColor: `${c}50` } : {}}
            data-testid={`${testidPrefix}-${id}`}
          >
            {labelOf(it)}
          </button>
        );
      })}
    </div>
  );
}

function LinkedRecipePicker({ excludeId, selectedIds, onChange }) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Fetched details for currently-selected recipes (so the stacked list always
  // shows the correct names, even when those recipes are outside the parent
  // page's active filter set).
  const [selectedRecipes, setSelectedRecipes] = useState([]);

  // Server-side debounced search — independent of parent page filters so any
  // recipe in the database can be linked, regardless of which category/kitchen
  // filter is active on the AdminRecipes list view.
  /* eslint-disable */
  useEffect(() => {
    let cancelled = false;
    const needle = query.trim();
    if (!needle) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams();
        params.set("q", needle);
        params.set("is_archived", "false");
        const r = await fetch(`${API}/recipes?${params.toString()}`, { credentials: "include" });
        const rows = r.ok ? await r.json() : [];
        if (!cancelled) setResults(rows);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);
  /* eslint-enable */

  // Fetch full details for every currently-selected linked recipe so the
  // stacked display works even when the parent's `recipes` array doesn't
  // contain them (e.g. when a filter is active on the list page).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedIds || selectedIds.length === 0) { if (!cancelled) setSelectedRecipes([]); return; }
      const rows = await Promise.all(
        selectedIds.map((rid) =>
          fetch(`${API}/recipes/${rid}`, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        ),
      );
      if (!cancelled) setSelectedRecipes(rows.filter(Boolean));
    })();
    return () => { cancelled = true; };
  }, [selectedIds]);

  const candidates = results
    .filter((r) => r.recipe_id !== excludeId && !selectedIds.includes(r.recipe_id))
    .slice(0, 12);

  const add = (rid) => {
    if (!selectedIds.includes(rid)) onChange([...selectedIds, rid]);
    setQuery("");
    // NOTE: do NOT setFocused(false) — the option click uses onMouseDown
    // preventDefault to retain DOM focus on the input. Forcing focused=false here
    // would leave the input DOM-focused but state-blurred, so the next keystroke
    // wouldn't show the dropdown until the user manually re-clicked the input.
  };
  const remove = (rid) => onChange(selectedIds.filter((x) => x !== rid));

  const needle = query.trim();
  const showDropdown = focused && needle.length > 0;

  return (
    <div className="space-y-2" data-testid="recipe-form-linked-picker">
      <div className="relative">
        <div className="relative">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            placeholder="Type to search recipes…"
            className="pl-8 text-xs"
            data-testid="recipe-form-linked-search"
          />
        </div>
        {showDropdown && (
          searching ? (
            <div className="absolute z-40 mt-1 w-full bg-white border border-gray-100 rounded-lg shadow-md p-2 text-[11px] text-gray-400 flex items-center gap-2" data-testid="recipe-form-linked-loading">
              <Loader2 size={11} className="animate-spin" /> Searching…
            </div>
          ) : candidates.length === 0 ? (
            <div className="absolute z-40 mt-1 w-full bg-white border border-gray-100 rounded-lg shadow-md p-2 text-[11px] text-gray-400" data-testid="recipe-form-linked-empty">
              No matching recipes
            </div>
          ) : (
            <ul className="absolute z-40 mt-1 w-full bg-white border border-gray-100 rounded-lg shadow-md max-h-56 overflow-y-auto" data-testid="recipe-form-linked-results">
              {candidates.map((r) => (
                <li key={r.recipe_id}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => add(r.recipe_id)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 flex items-center gap-2"
                    data-testid={`recipe-form-linked-option-${r.recipe_id}`}
                  >
                    <Link2 size={11} className="text-emerald-600" />
                    <span className="font-semibold text-gray-700">{r.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )
        )}
      </div>

      {selectedRecipes.length > 0 && (
        <ul className="space-y-1.5" data-testid="recipe-form-linked-selected">
          {selectedRecipes.map((r) => (
            <li
              key={r.recipe_id}
              className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
              data-testid={`recipe-form-linked-selected-${r.recipe_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Link2 size={12} className="text-emerald-600 flex-shrink-0" />
                <span className="text-xs font-semibold text-emerald-900 truncate">{r.name}</span>
              </div>
              <button
                type="button"
                onClick={() => remove(r.recipe_id)}
                className="text-emerald-700 hover:text-red-600 flex-shrink-0"
                aria-label={`Remove ${r.name}`}
                data-testid={`recipe-form-linked-remove-${r.recipe_id}`}
              >
                <XIcon size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
